/**
 * TimingTable — Hourly average timing breakdown table.
 * Columns: Create→Start, Start→Complete, Create→Complete, Andon Response
 */

import { andonLifecyclesFrom } from './andonLifecycle.js';

export const ANDON_SUFFIXES = [
    'GATE_ASSISTANCE', 'SEAL_CUTTING', 'DOCK_DOOR_ASSISTANCE', 'CHECK_IN_ASSISTANCE',
    'CHECK_OUT_REJECTION_ASSISTANCE', 'SEAL_VALIDATION', 'SEAL_CUTTING_AND_DOCK_DOOR_ASSISTANCE',
    'SPOT_CHECK', 'EQUIPMENT_LOCATION_CHECK', 'MISSING_CHECK_IN_CHECK', 'EQUIPMENT_CONTENT_CHECK',
    'REJECT_CHECK_IN_NO_EXIT_ALERT', 'INTRUSION_DETECTION_CHECK', 'YARD_MARSHALL_ASSISTANCE',
    'MECH_DEFECT_VALIDATION_ASSISTANCE'
];

export const ANDON_EVENT_TYPES = ANDON_SUFFIXES.flatMap(suffix =>
    ['CREATE', 'START', 'COMPLETE', 'CANCEL', 'ABANDON'].map(prefix => `${prefix}_${suffix}`)
);

export function fmtDuration(totalSeconds) {
    if (totalSeconds == null) return '';
    const hours   = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = num => String(num).padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

export function avgSecs(durations) {
    return durations.length
        ? Math.round(durations.reduce((sum, val) => sum + val, 0) / durations.length)
        : null;
}

// Internal alias used by row-computation functions
const _avgSecs = avgSecs;

export const DOCK_EVENT_TYPES = ['TDR_DOCK', 'TDR_RELEASE', 'DETACH_HAUL_ACTION'];

/**
 * Heat ratio for an hourly average against the metric's raw max, in [0, 1]
 * (low → light, high → strong; the timing cards render it with
 * `heatRatioCell`). Returns null when there is no average or no max, and the
 * card paints its theme gray.
 * @param {number|null} avgSecs
 * @param {number} maxAvgSecs
 * @returns {number|null}
 */
export function negativeHeatRatio(avgSecs, maxAvgSecs) {
    if (avgSecs == null || maxAvgSecs === 0) return null;
    return Math.min(avgSecs / maxAvgSecs, 1);
}

/**
 * Compute per-hour dock timing rows.
 * @param {Array<{create, start, complete}>} pairs
 * @param {Array} dockEvents - TDR_DOCK, TDR_RELEASE, DETACH_HAUL_ACTION events
 * @param {number} startHour
 * @param {number} startDateMs
 * @param {Array<{create, start, complete}>} [priorPairs=[]] - Pre-window pairs for dock timing lookback only
 */
export function buildDockTimingRows(pairs, dockEvents, startHour, startDateMs, priorPairs = [], numHours = 24) {
    const pad = num => String(num).padStart(2, '0');
    const buckets = Array.from({ length: numHours }, () => ({
        completeToTdrDock: [], loadCompleteToCreate: [], tdrReleaseToCreate: [], loadCompleteToCreateOver30: 0,
        // Retained contributors per metric field. loadCompleteToCreateOver30 here is an
        // ARRAY of the over-30m Detach→Create contributors — distinct from the sibling
        // numeric `loadCompleteToCreateOver30` counter above.
        _contributors: {
            completeToTdrDock:          [],
            loadCompleteToCreate:       [],
            tdrReleaseToCreate:         [],
            loadCompleteToCreateOver30: [],
        },
    }));

    const tdrDocks    = dockEvents.filter(event => event.eventType === 'TDR_DOCK').sort((a, b) => a.timestamp - b.timestamp);
    const tdrReleases = dockEvents.filter(event => event.eventType === 'TDR_RELEASE');
    const detachEvents = dockEvents.filter(event => event.eventType === 'DETACH_HAUL_ACTION');
    // UPDATE_EQUIPMENT_EMPTY_STATUS marks the trailer being (re)loaded. A load between a
    // detach and its create means the create is "move a freshly-loaded trailer", which the
    // Detach→Create metric should NOT count — it only measures detach-then-left-sitting-then-moved.
    const loadStatusEvents = dockEvents.filter(event => event.eventType === 'UPDATE_EQUIPMENT_EMPTY_STATUS');

    // Combine in-window pairs with prior-window pairs for Complete→TDR Dock matching,
    // but only in-window pairs are used for create-anchored metrics below
    const allPairsForDock = [...pairs, ...priorPairs];
    const inWindowPairSet = new Set(pairs);

    // Build sorted create timestamps per vehicle for intervening-create guards
    const createsByVehicle = new Map();
    for (const pair of pairs) {
        if (!pair.create) continue;
        const vn = pair.create.vehicleNumber;
        if (!createsByVehicle.has(vn)) createsByVehicle.set(vn, []);
        createsByVehicle.get(vn).push(pair.create.timestamp);
    }
    for (const arr of createsByVehicle.values()) arr.sort((a, b) => a - b);

    // Build a sorted list of all complete timestamps per vehicle so we can check for closer completes
    const completesByVehicle = new Map();
    for (const pair of allPairsForDock) {
        if (!pair.complete) continue;
        const vn = pair.complete.vehicleNumber;
        if (!completesByVehicle.has(vn)) completesByVehicle.set(vn, []);
        completesByVehicle.get(vn).push(pair.complete.timestamp);
    }
    for (const arr of completesByVehicle.values()) arr.sort((a, b) => a - b);

    for (const pair of allPairsForDock) {
        // Complete→TDR Dock: bucket by dock event time
        if (pair.complete) {
            const tdrDock = tdrDocks.find(event =>
                event.vehicleNumber === pair.complete.vehicleNumber &&
                event.timestamp >= pair.complete.timestamp
            );
            if (tdrDock) {
                const vehicleCompletes = completesByVehicle.get(pair.complete.vehicleNumber) || [];
                const closerComplete = vehicleCompletes.find(ts =>
                    ts > pair.complete.timestamp && ts <= tdrDock.timestamp
                );
                if (!closerComplete) {
                    const dockOffsetMs = tdrDock.timestamp * 1000 - startDateMs;
                    const windowMs = numHours * 3600000;
                    if (dockOffsetMs >= 0 && dockOffsetMs < windowMs) {
                        const dockHourIndex = Math.floor(dockOffsetMs / 3600000);
                        const gap = tdrDock.timestamp - pair.complete.timestamp;
                        buckets[dockHourIndex].completeToTdrDock.push(gap);
                        // Retain into the SAME dock-event-time bucket the average went into
                        // (NOT the create-time bucket). complete may be a pre-window priorPair.
                        buckets[dockHourIndex]._contributors.completeToTdrDock.push({
                            complete: pair.complete,
                            tdrDock,
                            duration: gap,
                            vehicleNumber: pair.complete.vehicleNumber,
                            userId: pair.complete.userId,
                            startTs: pair.complete.timestamp,
                            endTs: tdrDock.timestamp,
                        });
                    }
                }
            }
        }

        // Remaining metrics are anchored to create time — only for in-window pairs
        if (!pair.create) continue;
        if (!inWindowPairSet.has(pair)) continue;
        const offsetMs  = pair.create.timestamp * 1000 - startDateMs;
        const windowMs = numHours * 3600000;
        if (offsetMs < 0 || offsetMs >= windowMs) continue;
        const hourIndex = Math.floor(offsetMs / 3600000);
        if (hourIndex < 0 || hourIndex >= numHours) continue;
        const bucket = buckets[hourIndex];

        // Detach→Hostler Create: most recent DETACH_HAUL_ACTION before create for same vehicle,
        // but only if no closer create for the same vehicle sits between the detach and this create
        const vehicleCreates = createsByVehicle.get(pair.create.vehicleNumber) || [];
        const detach = [...detachEvents]
            .filter(event => event.vehicleNumber === pair.create.vehicleNumber && event.timestamp <= pair.create.timestamp)
            .sort((eventA, eventB) => eventB.timestamp - eventA.timestamp)[0];
        if (detach) {
            const closerCreate = vehicleCreates.find(ts => ts > detach.timestamp && ts < pair.create.timestamp);
            // Exclude if the trailer was (re)loaded between detach and create: this create is
            // moving a freshly-loaded trailer, not a trailer that was detached and left sitting.
            const loadedAfterDetach = loadStatusEvents.some(event =>
                event.vehicleNumber === pair.create.vehicleNumber &&
                event.timestamp > detach.timestamp &&
                event.timestamp < pair.create.timestamp
            );
            if (!closerCreate && !loadedAfterDetach) {
                const gap = pair.create.timestamp - detach.timestamp;
                bucket.loadCompleteToCreate.push(gap);
                const contributor = {
                    detach,
                    create: pair.create,
                    duration: gap,
                    vehicleNumber: pair.create.vehicleNumber,
                    userId: pair.create.userId,
                    startTs: detach.timestamp,
                    endTs: pair.create.timestamp,
                };
                bucket._contributors.loadCompleteToCreate.push(contributor);
                if (gap > 1800) {
                    bucket.loadCompleteToCreateOver30++;
                    // Same object reference so the >30m list and the count cannot drift.
                    bucket._contributors.loadCompleteToCreateOver30.push(contributor);
                }
            }
        }

        // TDR Release→Hostler Create: most recent TDR_RELEASE before create for same vehicle,
        // with the same intervening-create guard
        const tdrRelease = [...tdrReleases]
            .filter(event => event.vehicleNumber === pair.create.vehicleNumber && event.timestamp <= pair.create.timestamp)
            .sort((eventA, eventB) => eventB.timestamp - eventA.timestamp)[0];
        if (tdrRelease) {
            const closerCreate = vehicleCreates.find(ts => ts > tdrRelease.timestamp && ts < pair.create.timestamp);
            if (!closerCreate) {
                const gap = pair.create.timestamp - tdrRelease.timestamp;
                bucket.tdrReleaseToCreate.push(gap);
                bucket._contributors.tdrReleaseToCreate.push({
                    tdrRelease,
                    create: pair.create,
                    duration: gap,
                    vehicleNumber: pair.create.vehicleNumber,
                    userId: pair.create.userId,
                    startTs: tdrRelease.timestamp,
                    endTs: pair.create.timestamp,
                });
            }
        }
    }

    // Pre-compute max values for conditional formatting
    const allCompleteToTdrDock   = buckets.flatMap(bucket => bucket.completeToTdrDock);
    const allLoadCompleteToCreate = buckets.flatMap(bucket => bucket.loadCompleteToCreate);
    const allTdrReleaseToCreate  = buckets.flatMap(bucket => bucket.tdrReleaseToCreate);
    const maxCompleteToTdrDock    = allCompleteToTdrDock.length   ? Math.max(...allCompleteToTdrDock.map(val => val))   : 0;
    const maxLoadCompleteToCreate = allLoadCompleteToCreate.length ? Math.max(...allLoadCompleteToCreate.map(val => val)) : 0;
    const maxTdrReleaseToCreate  = allTdrReleaseToCreate.length  ? Math.max(...allTdrReleaseToCreate.map(val => val))  : 0;

    return Array.from({ length: numHours }, (_, slotIndex) => {
        const hourOfDay = (startHour + slotIndex) % 24;
        const bucket    = buckets[slotIndex];
        const avgCompleteToTdrDock    = _avgSecs(bucket.completeToTdrDock);
        const avgLoadCompleteToCreate = _avgSecs(bucket.loadCompleteToCreate);
        const avgTdrReleaseToCreate  = _avgSecs(bucket.tdrReleaseToCreate);
        return {
            hour:                       `${pad(hourOfDay)}:00`,
            completeToTdrDock:           avgCompleteToTdrDock,
            loadCompleteToCreate:       avgLoadCompleteToCreate,
            tdrReleaseToCreate:         avgTdrReleaseToCreate,
            loadCompleteToCreateOver30: bucket.loadCompleteToCreateOver30,
            _completeToTdrDockRatio:     negativeHeatRatio(avgCompleteToTdrDock,    maxCompleteToTdrDock),
            _loadCompleteToCreateRatio: negativeHeatRatio(avgLoadCompleteToCreate, maxLoadCompleteToCreate),
            _tdrReleaseToCreateRatio:   negativeHeatRatio(avgTdrReleaseToCreate,  maxTdrReleaseToCreate),
            _contributors:              bucket._contributors,
        };
    });
}

/**
 * Compute per-hour move/andon timing rows.
 * @param {Array<{create, start, complete}>} pairs
 * @param {Array} andonEvents - raw andon lifecycle events (fallback when no lifecycles given)
 * @param {number} startHour
 * @param {number} startDateMs
 * @param {number} [numHours=24]
 * @param {Array|null} [andonLifecycles=null] - lifecycles published by PerformanceDataService
 */
export function buildTimingRows(pairs, andonEvents, startHour, startDateMs, numHours = 24, andonLifecycles = null) {
    const pad = num => String(num).padStart(2, '0');
    const buckets = Array.from({ length: numHours }, () => ({
        createToStart: [], startToComplete: [], createToComplete: [], andonResponse: [],
        // Retained contributors per metric field (reference the input events, no clone).
        _contributors: {
            createToStart: [], startToComplete: [], createToComplete: [], andonResponse: []
        },
    }));
    // Accumulate all raw durations for true footer average
    const allDurations = { createToStart: [], startToComplete: [], createToComplete: [], andonResponse: [] };

    const windowMs = numHours * 3600000;
    for (const pair of pairs) {
        // Bucket by complete time — include all moves completed within the window
        const completeOffsetMs = pair.complete.timestamp * 1000 - startDateMs;
        if (completeOffsetMs < 0 || completeOffsetMs >= windowMs) continue;
        const hourIndex = Math.floor(completeOffsetMs / 3600000);
        if (hourIndex < 0 || hourIndex >= numHours) continue;
        const bucket = buckets[hourIndex];

        // S2C is always available for paired moves
        const s2c = pair.complete.timestamp - pair.start.timestamp;
        bucket.startToComplete.push(s2c);
        allDurations.startToComplete.push(s2c);
        bucket._contributors.startToComplete.push({ start: pair.start, complete: pair.complete, duration: s2c });

        // C2S and C2C require a create event
        if (pair.create) {
            const c2s = pair.start.timestamp - pair.create.timestamp;
            const c2c = pair.complete.timestamp - pair.create.timestamp;
            bucket.createToStart.push(c2s);
            bucket.createToComplete.push(c2c);
            allDurations.createToStart.push(c2s);
            allDurations.createToComplete.push(c2c);
            bucket._contributors.createToStart.push({ create: pair.create, start: pair.start, complete: pair.complete, duration: c2s });
            bucket._contributors.createToComplete.push({ create: pair.create, start: pair.start, complete: pair.complete, duration: c2c });
        }
    }

    // START → COMPLETE of the same andon lifecycle (shared order-independent resolver).
    // Prefer lifecycles published by PerformanceDataService; fall back to resolving andonEvents.
    const lifecycles = andonLifecyclesFrom({ andonLifecycles, andonEvents }, ANDON_SUFFIXES);
    for (const lc of lifecycles) {
        if (lc.status !== 'completed' || !lc.start) continue;
        const offsetMs  = lc.complete.timestamp * 1000 - startDateMs;
        if (offsetMs < 0 || offsetMs >= windowMs) continue;
        const hourIndex = Math.floor(offsetMs / 3600000);
        if (hourIndex >= 0 && hourIndex < numHours) {
            const dur = lc.responseSecs;
            buckets[hourIndex].andonResponse.push(dur);
            allDurations.andonResponse.push(dur);
            buckets[hourIndex]._contributors.andonResponse.push({
                start: lc.start, complete: lc.complete, suffix: lc.suffix, duration: dur,
            });
        }
    }

    // Per-metric RAW max (same basis as the dock builder: color each hour's average
    // against the max of the raw per-item durations, not the max of the averages).
    const maxCreateToStart    = allDurations.createToStart.length    ? Math.max(...allDurations.createToStart)    : 0;
    const maxStartToComplete  = allDurations.startToComplete.length  ? Math.max(...allDurations.startToComplete)  : 0;
    const maxCreateToComplete = allDurations.createToComplete.length ? Math.max(...allDurations.createToComplete) : 0;
    const maxAndonResponse    = allDurations.andonResponse.length    ? Math.max(...allDurations.andonResponse)    : 0;

    const rows = Array.from({ length: numHours }, (_, slotIndex) => {
        const hourOfDay = (startHour + slotIndex) % 24;
        const bucket    = buckets[slotIndex];
        const avgCreateToStart    = _avgSecs(bucket.createToStart);
        const avgStartToComplete  = _avgSecs(bucket.startToComplete);
        const avgCreateToComplete = _avgSecs(bucket.createToComplete);
        const avgAndonResponse    = _avgSecs(bucket.andonResponse);
        return {
            hour:             `${pad(hourOfDay)}:00`,
            createToStart:    avgCreateToStart,
            startToComplete:  avgStartToComplete,
            createToComplete: avgCreateToComplete,
            andonResponse:    avgAndonResponse,
            _createToStartRatio:    negativeHeatRatio(avgCreateToStart,    maxCreateToStart),
            _startToCompleteRatio:  negativeHeatRatio(avgStartToComplete,  maxStartToComplete),
            _createToCompleteRatio: negativeHeatRatio(avgCreateToComplete, maxCreateToComplete),
            _andonResponseRatio:    negativeHeatRatio(avgAndonResponse,    maxAndonResponse),
            _contributors:          bucket._contributors,
        };
    });

    // Attach raw durations for true footer average computation
    rows._allDurations = allDurations;
    return rows;
}
